import { FieldsFetcherService, FieldsScopeKey } from '@vdms-hq/fields';

import { SelectOption, SelectOptionKey } from '@vdms-hq/shared';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, defer, EMPTY, from, Observable, of, switchMap } from 'rxjs';
import { ActivatedRouteSnapshot, Resolve } from '@angular/router';
import { catchError, filter, map, take } from 'rxjs/operators';
import { AuthService } from '@vdms-hq/auth';
import { resultsAdminConfig, ResultsAdminConfig } from '../../../config';
import { ColumnFieldGroup, FieldDefinitionModel } from '@vdms-hq/api-contract';
import {
  ClientAdminActionService,
  ClientAdminDataSourceService,
  ClientAdminViewModel,
  InheritableClientViewModel,
} from '@vdms-hq/client-admin-core';

export type FormSettings = {
  canRemoveFromAvailable: boolean;
  canRevertToDefault: boolean;
  isInheritedFromDefault: boolean;
  isInheritedFromClient: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class AdminFieldsConfigService implements Resolve<FieldsScopeKey | null> {
  #fieldsFetcher = inject(FieldsFetcherService);
  #authService = inject(AuthService);
  #clientAdminDataSource = inject(ClientAdminDataSourceService);
  #clientAdminActionService = inject(ClientAdminActionService);

  #currentScopeKey = new BehaviorSubject<ResultsAdminConfig | null>(null);

  currentScopeKey$ = this.#currentScopeKey.pipe(filter((key) => key !== null));

  /** Inheritable (base/template) clients — shared app-wide via ClientAdminDataSourceService. */
  #inheritableClients$: Observable<InheritableClientViewModel[]> = defer(
    () => this.#clientAdminDataSource.inheritableClients$,
  );

  /** Admin ViewModel of the active client — shared app-wide via ClientAdminDataSourceService. */
  #adminClient$: Observable<ClientAdminViewModel> = defer(() => this.#clientAdminDataSource.currentAdminClient$);

  configForTable$: Observable<{
    defaultList: SelectOptionKey[];
    availableList: SelectOptionKey[];
    outsideList: SelectOptionKey[];
    all: SelectOption[];
    settings: FormSettings;
  }> = this.#currentScopeKey.pipe(
    filter(Boolean),
    switchMap((scopeSettings) =>
      combineLatest([this.#fieldsFetcher.getConfigurationLive$(scopeSettings.key), this.#adminClient$]).pipe(
        map(([data, client]) => {
          const systemList = data.table.getSystemKeys();
          const defaultList = data.table.getClientEnabledKeys();
          const availableList = this.#excludeFromList(data.table.getClientAvailableKeys(), defaultList);
          const outsideList = this.#excludeFromList(systemList, [...defaultList, ...availableList]);

          return {
            defaultList: defaultList,
            availableList: availableList,
            outsideList: outsideList,
            all: this.#defsToSelectOptionsWithGroup(data.table.getSystemDefs()),
            settings: {
              canRemoveFromAvailable: true,
              canRevertToDefault: !data.table.client.isInherited,
              isInheritedFromDefault: scopeSettings.key !== 'default' && data.table.client.isInherited,
              isInheritedFromClient: !!this.#resolveInheritedSource(
                client.resultsSettings.inheritance,
                scopeSettings.key,
              ),
            },
          };
        }),
      ),
    ),
  );

  configForList$: Observable<{
    defaultList: SelectOptionKey[];
    availableList: SelectOptionKey[];
    outsideList: SelectOptionKey[];
    all: SelectOption[];
    settings: FormSettings;
  }> = this.currentScopeKey$.pipe(
    switchMap((scopeSettings) =>
      combineLatest([this.#fieldsFetcher.getConfigurationLive$(scopeSettings.key), this.#adminClient$]).pipe(
        map(([data, client]) => {
          const systemList = data.list.getSystemKeys();
          const defaultList = data.list.getClientEnabledKeys();
          const availableList = this.#excludeFromList(systemList, defaultList);
          const outsideList = this.#excludeFromList(systemList, [...defaultList, ...availableList]);

          return {
            defaultList,
            availableList: availableList,
            outsideList: outsideList,
            all: this.#defsToSelectOptionsWithGroup(data.list.system),
            settings: {
              canRemoveFromAvailable: false,
              canRevertToDefault: !data.list.client.isInherited,
              isInheritedFromDefault: scopeSettings.key !== 'default' && data.list.client.isInherited,
              isInheritedFromClient: !!this.#resolveInheritedSource(
                client.resultsSettings.inheritance,
                scopeSettings.key,
              ),
            },
          };
        }),
      ),
    ),
  );

  configForFilters$: Observable<{
    defaultList: SelectOptionKey[];
    availableList: SelectOptionKey[];
    outsideList: SelectOptionKey[];
    all: SelectOption[];
    settings: FormSettings;
  }> = this.#currentScopeKey.pipe(
    filter(Boolean),
    switchMap((scopeSettings) =>
      combineLatest([this.#fieldsFetcher.getConfigurationLive$(scopeSettings.key), this.#adminClient$]).pipe(
        map(([data, client]) => {
          const systemList = data.filters.getSystemKeys();
          const defaultList = data.filters.getEnabledKeys();
          const availableList = this.#excludeFromList(data.filters.getClientAvailableKeys(), defaultList);
          const outsideList = this.#excludeFromList(systemList, [...defaultList, ...availableList]);

          return {
            defaultList,
            availableList: availableList,
            outsideList: outsideList,
            all: this.#defsToSelectOptionsWithGroup(data.filters.system),
            settings: {
              canRemoveFromAvailable: true,
              canRevertToDefault: !data.filters.client.isInherited,
              isInheritedFromDefault: scopeSettings.key !== 'browse' && data.filters.client.isInherited,
              isInheritedFromClient: !!this.#resolveInheritedSource(
                client.resultsSettings.inheritance,
                scopeSettings.key,
              ),
            },
          };
        }),
      ),
    ),
  );

  resolve(route: ActivatedRouteSnapshot) {
    const configKey = route.params.configKey as FieldsScopeKey;
    const config = resultsAdminConfig.find((item) => item.key === configKey);

    if (!config) {
      return null;
    }
    this.#currentScopeKey.next(config);

    return config.key;
  }

  /**
   * Clears the stored scope key when the user navigates away from the admin
   * tables-and-lists route. This prevents stale scope state from leaking into
   * subsequent visits. Should be called from the route's canDeactivate guard.
   */
  reset() {
    this.#currentScopeKey.next(null);
  }

  revertToDefaultList$(): Observable<boolean> {
    return this.currentScopeKey$.pipe(
      take(1),
      switchMap((scope) => this.#mutateAndSave$((model) => model.resultsSettings.revertList(scope.key))),
    );
  }

  revertToDefaultTable$(): Observable<boolean> {
    return this.currentScopeKey$.pipe(
      take(1),
      switchMap((scope) => this.#mutateAndSave$((model) => model.resultsSettings.revertColumns(scope.key))),
    );
  }

  revertToDefaultFilters$(): Observable<boolean> {
    return this.currentScopeKey$.pipe(
      take(1),
      switchMap((scope) => this.#mutateAndSave$((model) => model.resultsSettings.revertFilters(scope.key))),
    );
  }

  #excludeFromList(list: string[], exclusions: string[]) {
    return list.filter((item) => !exclusions.includes(item));
  }

  /**
   * Resolves the inheritance source-client UUID for a scope, honoring the backend's
   * wildcard: a `'*'` entry means "all scopes inherit from this client", while a
   * specific scope key overrides it for that scope (precedence: scope key > `'*'`).
   * Returns null when neither is set.
   */
  #resolveInheritedSource(inheritance: Record<string, string> | null | undefined, scopeKey: string): string | null {
    return inheritance?.[scopeKey] ?? inheritance?.['*'] ?? null;
  }

  #defsToSelectOptionsWithGroup(defs: FieldDefinitionModel[]): SelectOption[] {
    return defs.map((def) => ({
      key: def.id,
      label: def.label,
      additional_label: def.columnFieldGroup,
      // Used by list-item border color (inline CSS color value).
      suffix: this.borderColorForGroup(def.columnFieldGroup),
      // Used by form-input-select pills (CSS token class name).
      suffix_color: this.colorForGroup(def.columnFieldGroup),
    }));
  }

  colorForGroup(group: ColumnFieldGroup | undefined): string {
    switch (group) {
      case ColumnFieldGroup.ACTIONS:
        return 'failed';
      case ColumnFieldGroup.TECHNICAL_SPECIFICATION:
        return 'done';
      case ColumnFieldGroup.ASSET_METADATA:
        return 'yellow';
      case ColumnFieldGroup.INTERNAL_METADATA:
        return 'orange';
      default:
        return 'grey-light';
    }
  }

  borderColorForGroup(group: ColumnFieldGroup | undefined): string {
    switch (group) {
      case ColumnFieldGroup.ACTIONS:
        return '#bd2b3c'; // $failed
      case ColumnFieldGroup.TECHNICAL_SPECIFICATION:
        return '#05bb73'; // $done
      case ColumnFieldGroup.ASSET_METADATA:
        return '#eaa800'; // $yellow
      case ColumnFieldGroup.INTERNAL_METADATA:
        return '#f06500'; // $orange
      default:
        return '#868686'; // $grey-30
    }
  }

  /**
   * Emits the UUID of the source client for the currently active scope,
   * or null when no inheritance is set.
   */
  inheritedFromClientUuid$: Observable<string | null> = combineLatest([this.currentScopeKey$, this.#adminClient$]).pipe(
    map(([scope, client]) => this.#resolveInheritedSource(client.resultsSettings.inheritance, scope.key)),
  );

  /**
   * Emits the display name of the source client for the currently active scope,
   * or null when no inheritance is set.
   */
  inheritedFromClientName$: Observable<string | null> = combineLatest([
    this.inheritedFromClientUuid$,
    this.#inheritableClients$,
  ]).pipe(map(([uuid, clients]) => (uuid ? (clients.find((c) => c.uuid === uuid)?.name ?? null) : null)));

  /**
   * True when the active scope is inherited ONLY via the wildcard `'*'` (no specific
   * scope entry). Per-scope removal can't undo a wildcard — the inheritance map has no
   * "not inherited" value — so the Remove action is disabled for these scopes.
   */
  isWildcardInherited$: Observable<boolean> = combineLatest([this.currentScopeKey$, this.#adminClient$]).pipe(
    map(([scope, client]) => {
      const inheritance = client.resultsSettings.inheritance;
      return !inheritance?.[scope.key] && !!inheritance?.['*'];
    }),
  );

  /** All inheritable clients as dropdown options (the active client is already excluded server-side). */
  clientsSelectOptions$: Observable<SelectOption[]> = this.#inheritableClients$.pipe(
    map((clients) => [
      { key: null, label: 'N/A' },
      ...clients.map((c) => ({ key: c.uuid, label: c.name })).sort((a, b) => a.label.localeCompare(b.label)),
    ]),
  );

  /**
   * Saves only the inheritance reference for the active scope.
   * Does not touch any columns/filters/vida data.
   */
  setInheritance$(clientUuid: string): Observable<void> {
    return this.currentScopeKey$.pipe(
      take(1),
      switchMap((scope) =>
        this.#mutateAndSave$((model) => model.resultsSettings.setInheritance(scope.key, clientUuid)),
      ),
      map(() => undefined),
    );
  }

  /**
   * Removes inheritance for the active scope, keeping the source client's last-resolved
   * configuration as the client's own editable copy (#15949): materializes the source's
   * per-scope config onto the ViewModel, then drops the scope's inheritance pointer.
   * When the source client can't be resolved only the pointer is dropped — the local
   * config is what the admin was seeing anyway.
   */
  removeInheritance$(): Observable<void> {
    return combineLatest([this.currentScopeKey$, this.#inheritableClients$]).pipe(
      take(1),
      switchMap(([scope, inheritableClients]) =>
        this.#mutateAndSave$((model) => {
          const sourceUuid = this.#resolveInheritedSource(model.resultsSettings.inheritance, scope.key);
          const source = inheritableClients.find((client) => client.uuid === sourceUuid)?.resultsSettings;
          model.resultsSettings.removeInheritance(scope.key, source);
        }),
      ),
      map(() => undefined),
    );
  }

  /**
   * Detaches the client from wildcard (`'*'`) Results Settings inheritance, keeping the
   * last-resolved configuration of every scope as an editable copy (#15949), then drops
   * the whole `resultsSettingsInheritance` map. Errors (before anything is written) when
   * a referenced source client can't be resolved — see
   * {@link ResultsSettingsViewModel.removeAllInheritance}.
   */
  removeWildcardInheritance$(): Observable<void> {
    return this.#inheritableClients$.pipe(
      take(1),
      switchMap((inheritableClients) =>
        this.#mutateAndSave$((model) =>
          model.resultsSettings.removeAllInheritance(
            (uuid) => inheritableClients.find((client) => client.uuid === uuid)?.resultsSettings,
          ),
        ),
      ),
      map(() => undefined),
    );
  }

  /** Mutates the current admin client's ViewModel in place, then persists it via the shared save pipeline. */
  #mutateAndSave$(mutate: (model: ClientAdminViewModel) => void): Observable<boolean> {
    const model = this.#clientAdminDataSource.currentAdminClient();
    if (!model) return EMPTY;

    mutate(model);
    return this.#save$(model);
  }

  /**
   * Persists the ViewModel via `ClientAdminActionService#save` (`PATCH /group/{uuid}/config`),
   * then refreshes `ClientAdminDataSourceService` so `#adminClient$` (and therefore
   * `configForTable$`/`configForList$`/`configForFilters$`) reflects the change immediately —
   * `currentAdminClient$` does not re-emit on its own after a save. Also refreshes
   * `/user/auth/me` for the rest of the app, which still reads the raw `clientDefinite$`.
   */
  #save$(model: ClientAdminViewModel): Observable<boolean> {
    return this.#clientAdminActionService.save(model).pipe(
      switchMap((success) =>
        success
          ? from(this.#authService.refreshAuthMe()).pipe(
              map(() => true),
              catchError(() => of(true)),
            )
          : of(false),
      ),
      map((success) => {
        if (success) {
          this.#clientAdminDataSource.refresh();
        }
        return success;
      }),
    );
  }
}
